"use client";

import AdListingModel from "@/models/AdListingModel";
import { Box, IconButton, Typography } from "@mui/material";
import useEmblaCarousel from "embla-carousel-react";
import ListingCard from "../listing/ListingCard";
import { useCallback, useEffect } from "react";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AutoScroll from "embla-carousel-auto-scroll";
import { BoxWithGutters } from "../common/BoxWithGutters";
import Link from "next/link";
import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";
import { CreateAnalyticEventType } from "@/models/CreateAnalyticEventModel";

const NORMAL_PADDING = 4;
const COMPACT_PADDING = 2;

export default function ListingCarousel({
  title,
  ads,
  autoScroll,
  size = "large",
  isOnGrey,
  titleHref,
  placement,
}: {
  title: string;
  ads: AdListingModel[];
  autoScroll?: boolean;
  size?: "large" | "medium" | "small";
  isOnGrey?: boolean;
  titleHref?: string;
  placement: CreateAnalyticEventType;
}) {
  const plugins = [];
  if (autoScroll) {
    plugins.push(AutoScroll({ speed: 1, startDelay: 500 }));
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: autoScroll,
      align: "start",
    },
    plugins,
  );

  const handleScrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi?.plugins()?.autoScroll?.stop();
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const handleScrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi?.plugins()?.autoScroll?.stop();
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  const handleMouseEnter = useCallback(() => {
    emblaApi?.plugins()?.autoScroll?.stop();
  }, [emblaApi]);

  const handleMouseLeave = useCallback(() => {
    emblaApi?.plugins()?.autoScroll?.play();
  }, [emblaApi]);

  useEffect(() => {
    if (ads) {
      CreateAnalyticEvent({
        event: {
          adIds: ads.map((ad) => ad.id),
          eventType: placement,
        },
      });
    }
  }, [ads, placement]);

  const Title = (
    <Typography variant={size === "large" ? "h5" : "h6"} component="h2">
      {title}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        paddingTop: size === "large" ? NORMAL_PADDING : COMPACT_PADDING,
      }}
    >
      <BoxWithGutters
        extraPaddingRight={size === "small"}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {titleHref ? (
          <Link
            href={titleHref}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {Title}
          </Link>
        ) : (
          Title
        )}
        <Box sx={{ marginLeft: "auto", display: "flex", gap: 1 }}>
          <IconButton
            className="embla__prev"
            onClick={handleScrollPrev}
            sx={{ backgroundColor: isOnGrey ? "white" : "grey.100" }}
          >
            <NavigateBeforeIcon sx={{ color: "black" }} />
          </IconButton>
          <IconButton
            className="embla__next"
            onClick={handleScrollNext}
            sx={{ backgroundColor: isOnGrey ? "white" : "grey.100" }}
          >
            <NavigateNextIcon sx={{ color: "black" }} />
          </IconButton>
        </Box>
      </BoxWithGutters>
      <BoxWithGutters
        className="embla"
        extraPaddingRight={size === "small"}
        sx={{
          overflow: "hidden",
        }}
      >
        <Box
          className="embla__viewport"
          sx={(theme) => ({
            overflow: "hidden",
            [theme.breakpoints.down("sm")]: {
              overflow: "visible",
            },
          })}
          ref={emblaRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Box
            className="embla__container"
            sx={{
              display: "flex",
              paddingTop: size === "large" ? NORMAL_PADDING : COMPACT_PADDING,
              paddingBottom: NORMAL_PADDING,
            }}
          >
            {ads.map((ad) => (
              <Box
                key={ad.id}
                className="embla__slide "
                sx={{
                  flex: `0 0 ${size === "small" ? 250 : 350}px`,
                  minWidth: 0,
                  height: "",
                  paddingRight: 3,
                }}
              >
                <ListingCard ad={ad} isCompact={size === "small"} />
              </Box>
            ))}
          </Box>
        </Box>
      </BoxWithGutters>
    </Box>
  );
}
